module PortalLibs.Client.Util


open System
open Fable.Core.JsInterop
open FSharpPlus
open Fable.Import.Moment
open Elmish.Navigation
open Fable.Remoting.Client
open Fable.FontAwesome
open Feliz
open Feliz.Bulma
open Fable.Core
open Browser
open PortalLibs.Shared

let forceReload () = window?location?reload()

let inline (=>) k v = k ==> v

let goToUrl (e: Browser.Types.MouseEvent) =
  e.preventDefault ()
  let href = !!e.target?href

  Navigation.newUrl href
  |>> (fun f -> f ignore)
  |> ignore

let ifEnter f (e: Browser.Types.KeyboardEvent) =
  if e.key = "Enter" then f e

let calcMaxDisplayWidth minWidth display lengthToPix (headerText: string) set =
  let maxContentLength =
    match set with
    | [] -> 0
    | _ ->
      set
      |>> (display >> (fun (s: string) -> s.Length))
      |> List.max


  let spacing = 30.0
  let maxLength = max maxContentLength headerText.Length
  max ((float maxLength) * lengthToPix + spacing) minWidth

let prettyPrintDateLong (date: DateTime) = Global.Invoke(date).format "LL"

let prettyPrintDateShort (date: DateTime) = Global.Invoke(date).format "L"

let prettyPrintDateDashes (date: DateTime) =
  Global.Invoke(date).format "YYYY-MM-DD"

let prettyPrintDateOptionLong =
  function
  | None -> String.Empty
  | Some x -> prettyPrintDateLong x

let prettyPrintDateOptionShort =
  function
  | None -> String.Empty
  | Some x -> prettyPrintDateShort x

let printLong (num: int64) = num.ToString()
let printDecimal (num: decimal) = num.ToString()

let isDateBefore (a: Moment) b = a.isBefore b
let isDateAfter (a: Moment) b = a.isAfter b

let compareDateOptions a b operator =
  match a with
  | None -> true
  | Some (dateA: DateTime) ->
    match b with
    | None -> false
    | Some (dateB: DateTime) -> operator (Global.Invoke(dateA)) (Global.Invoke(dateB))

let triggerPrint () =
  async {
    do! Async.Sleep 300
    window.print ()
    do! Async.Sleep 300
  }

let emptyStrTagIfNone<'T> (strOpt: Option<'T>) =
  strOpt |> Option.emptyStrIfNone |> Html.text

let fieldWithControlStyles (name: string) (controls: ReactElement list) styles =
  Bulma.field.div [
    prop.style [ style.textAlign.left ]
    prop.children [
      Bulma.label [ prop.text name ]
      Bulma.control.div [
        prop.style styles
        prop.children controls
      ]
    ]
  ]

let field (name: string) (controls: ReactElement list) = fieldWithControlStyles name controls []

let fieldHoriz (name: string) (controls: ReactElement list) =
  fieldWithControlStyles
    name
    controls
    [ style.display.flex
      style.alignItems.center ]


let scrollToTop () = window.scrollTo (0.0, 0.0)

let scrollTo y =
  JS.setTimeout (fun () -> window.scrollTo (0.0, y)) 100

let getDivScrollPos id =
  let div = document.getElementsByName id

  if div.length > 0 then
    let scroll = div.[0].scrollTop
    Some scroll
  else
    None

let scrollDivToPos id pos =
  let div = document.getElementsByName id

  if div.length > 0 then
    div.[0].scrollTop <- pos

let getTableScrollPos () =
  let div =
    document.getElementsByClassName "rt-tbody"

  if div.length > 0 then
    let scroll = div.[0].scrollTop
    Some scroll
  else
    None

let scrollTableToPos pos =
  JS.setTimeout
    (fun () ->
      let div =
        document.getElementsByClassName "rt-tbody"

      if div.length > 0 then
        div.[0].scrollTop <- pos)
    100
  |> ignore

let scrollIntoView id =
  JS.setTimeout
    (fun () ->
      let elem = document.getElementById id
      elem.scrollIntoView true)
    100
  |> ignore

let getWindowScrollValue () = Dom.window.scrollY

let authenticatingCompOrErrorMessage (e: exn) comp =
  match e with
  | :? ProxyRequestException as ex ->
    if ex.StatusCode = 401 then
      comp
    else
      [ Html.text "Error: "
        Html.text e.Message ]
  | _ ->
    [ Html.text "Error: "
      Html.text e.Message ]

let authenticatingOrErrorMessage (e: exn) =
  authenticatingCompOrErrorMessage e [ Html.text "Authentication required, redirecting..." ]

type IconSize =
  | Large
  | Small
  | Normal

let loadingWithElem (elem: ReactElement) size =
  Html.div [
    prop.style [ style.textAlign.center ]
    prop.children [
      Bulma.icon [
        match size with
        | Large -> icon.isLarge
        | Small -> icon.isSmall
        | Normal -> icon.isMedium
        prop.children [
          Fa.i [ Fa.Solid.Spinner
                 Fa.Pulse
                 match size with
                 | Large -> Fa.Size Fa.Fa2x
                 | Small -> Fa.Size Fa.FaSmall
                 | Normal -> () ] []
        ]
      ]
      Html.div [ elem ]
    ]
  ]

let smallSpinner = loadingWithElem Html.none IconSize.Small

let loadingWithMessage (message: String) =
  loadingWithElem (Html.text message) Large

let loadingWithMessageHoriz (message: String) =
  Html.div [
    prop.style [ style.display.flex ]
    prop.children [
      Bulma.icon [
        prop.children [
          Fa.i [ Fa.Solid.Spinner; Fa.Pulse ] []
        ]
      ]
      Html.text message
    ]
  ]

let loadingMessageOrContent message content option =
  match option with
  | None ->
    Html.div [
      prop.style [ style.height 300 ]
      prop.children (
        Html.div [
          prop.style [
            style.position.relative
            style.top (length.perc 30)
          ]
          prop.children (loadingWithMessage message)
        ]
      )
    ]
  | Some some -> content some

let spinnerOrContent' option size content =
  match option with
  | None -> loadingWithElem Html.none size
  | Some some -> content some

let nothingOrContent option content =
  match option with
  | None -> Html.none
  | Some some -> content some

let spinnerOrContent option content = spinnerOrContent' option Large content

let loadingMessageBoxOrContent option message content =
  Bulma.box (loadingMessageOrContent option message content)

let elemInBox (elem: ReactElement) =
  Bulma.box [
    prop.style [ style.height 300 ]
    prop.children (
      Html.div [
        prop.style [
          style.position.relative
          style.top (length.perc 30)
        ]
        prop.children elem
      ]
    )
  ]

let loadingMessageInBoxOrContent option message content =
  match option with
  | None -> elemInBox (loadingWithMessage message)
  | Some some -> content some


[<Import("", from = "rc-input-number")>]
let ReactNumericInput: obj = jsNative

let numericInputControl value valueChanged =
  let props =
    createObj [
      "min" => 0
      "value" => value
      "onChange"
      => (fun v ->
        printfn $"value changed %i{v}"
        valueChanged v)
      "className" => "input"
    ]

  Fable.React.Helpers.ofImport "default" "rc-input-number" props []


let reactFileViewer (url: string) =
  let inputProps =
    createObj [
      "fileType"
      => (url.Split(".") |> List.ofArray |> List.last)
      "filePath" => url
    ]

  Fable.React.Helpers.ofImport "default" "@studyworld/react-file-viewer" inputProps []

let inline valueOrDefault value (e: obj) =

  if e |> isNull |> not && !!e?value <> !!value then
    e?value <- !!value


let fieldWithHeader (widthOpt: int option) _styles (name: string) (field: ReactElement) =
  Html.div [
    prop.style [
      style.marginRight 15
      match widthOpt with
      | None -> ()
      | Some width -> style.width width
    ]
    prop.children [
      Html.div [
        prop.style [
          style.display.flex
          style.flexDirection.column
          style.height (length.perc 100)
        ]
        prop.children [
          Html.div [
            prop.style [
              style.flexGrow 1
              style.display.flex
              style.alignItems.center
              style.marginBottom 0
            ]
            prop.children [ Bulma.label name ]
          ]
          Bulma.control.div [ field ]
        ]
      ]
    ]
  ]

let textFieldReadOnlyStyles (widthOpt: int option) styles (name: string) (value: string) =
  fieldWithHeader
    widthOpt
    styles
    name
    (Bulma.control.div [
      Bulma.input.text [
        prop.style styles
        prop.value value
        prop.disabled true
      ]
     ])

let textFieldReadOnly = textFieldReadOnlyStyles (Some 200) []

let filterItemBySearchTerm<'T> (getters: ('T -> string) list) (term: string) (item: 'T) =
  match term with
  | "" -> true
  | x ->
    let matcher =
      List.fold (fun acc getter -> (acc + (getter item) + " ")) "" getters

    matcher.ToLower().Contains(x.ToLower())
    || List.fold (fun acc getter -> acc && (getter item) = "") true getters


[<ImportAll("body-scroll-toggle")>]
let bodyScrollToggle: obj = jsNative

let bodyScrollEnable () = !! bodyScrollToggle?enable ()

let bodyScrollDisable () = !! bodyScrollToggle?disable ()

let dateToSqlString (date: DateTime) =
  date.ToString("yyyy-MM-dd HH:mm:ss.fff")


let centeredElem elem =
  Html.div [
    prop.style [
      style.display.flex
      style.flexDirection.column
      style.alignItems.center
    ]
    prop.children [
      Html.span [
        prop.style [
          style.textAlign.center
          style.marginTop 30
        ]
        prop.children [ elem ]
      ]
    ]
  ]

let textMiddle (text: String) = centeredElem (Html.text text)